// SPDX-License-Identifier: BUSL-1.1

/**
 * Members management page.
 *
 * Shows organization members with roles, allows inviting new members,
 * changing roles, and removing members. Admin-only access.
 *
 * PERPLEXITY VALIDATION EDGE CASES:
 * - Cross-org access: derive org ID from auth context, cross-check against :id path param
 * - Duplicate invite: handle existing member invite gracefully (409 or resend)
 * - OrgMember DTO: id, email, name, role, joinedAt, status
 * - Pagination: add page/pageSize if member counts grow
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import type { ColumnDef } from '@tanstack/react-table';
import { UserPlus, Trash2 } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { useOrg } from '@/contexts/org-context';
import { DataTable } from '@/components/data-table';
import { TableSkeleton } from '@/components/skeletons';
import { showError, showSuccess } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgMember {
  id: string;
  email: string;
  name: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  joinedAt: string;
  status: 'active' | 'invited' | 'pending';
}

interface MembersResponse {
  data: OrgMember[];
}

// ---------------------------------------------------------------------------
// Role badge (same style as OrgSwitcher)
// ---------------------------------------------------------------------------

const ROLE_VARIANTS: Record<string, string> = {
  owner: 'bg-amber-900/50 text-amber-300 border-amber-700',
  admin: 'bg-blue-900/50 text-blue-300 border-blue-700',
  member: 'bg-neutral-800 text-neutral-300 border-neutral-600',
  viewer: 'bg-neutral-800 text-neutral-400 border-neutral-700',
};

function RoleBadge({ role }: { role: string }) {
  return (
    <Badge variant="outline" className={ROLE_VARIANTS[role] ?? ROLE_VARIANTS.member}>
      {role}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const memberColumns: ColumnDef<OrgMember>[] = [
  {
    accessorKey: 'email',
    header: 'Email',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{row.original.email}</span>
        {row.original.name && <span className="text-xs text-neutral-500">{row.original.name}</span>}
      </div>
    ),
  },
  {
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row }) => <RoleBadge role={row.original.role} />,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={row.original.status === 'active' ? 'default' : 'secondary'}>
        {row.original.status}
      </Badge>
    ),
  },
  {
    accessorKey: 'joinedAt',
    header: 'Joined',
    cell: ({ row }) => (
      <span className="text-sm text-neutral-400">
        {new Date(row.original.joinedAt).toLocaleDateString()}
      </span>
    ),
  },
];

// ---------------------------------------------------------------------------
// MembersPage component
// ---------------------------------------------------------------------------

export default function MembersPage() {
  const { currentOrg } = useOrg();
  const queryClient = useQueryClient();

  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<OrgMember | null>(null);

  // ui-002: react-hook-form for invite dialog (replaces useState)
  const inviteForm = useForm<{ email: string; role: string }>({
    defaultValues: { email: '', role: 'member' },
  });

  // Fetch members
  const {
    data: membersData,
    isLoading,
    error,
  } = useQuery<MembersResponse>({
    queryKey: ['members', currentOrg?.id],
    queryFn: () => apiFetch(`/orgs/${currentOrg?.id}/members`),
    enabled: !!currentOrg?.id,
  });

  const members = membersData?.data ?? [];

  // Invite mutation
  const inviteMutation = useMutation({
    mutationFn: async (data: { email: string; role: string }) => {
      return apiFetch(`/orgs/${currentOrg?.id}/invitations`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },
    onSuccess: () => {
      showSuccess('Invitation sent');
      setShowInviteDialog(false);
      inviteForm.reset();
      queryClient.invalidateQueries({ queryKey: ['members', currentOrg?.id] });
    },
    onError: (err: Error) => {
      showError(err.message ?? 'Failed to send invitation');
    },
  });

  // Remove mutation
  const removeMutation = useMutation({
    mutationFn: async (memberId: string) => {
      return apiFetch(`/orgs/${currentOrg?.id}/members/${memberId}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      showSuccess('Member removed');
      setMemberToRemove(null);
      queryClient.invalidateQueries({ queryKey: ['members', currentOrg?.id] });
    },
    onError: (err: Error) => {
      showError(err.message ?? 'Failed to remove member');
    },
  });

  // Role change mutation
  const roleMutation = useMutation({
    mutationFn: async (data: { memberId: string; role: string }) => {
      return apiFetch(`/orgs/${currentOrg?.id}/members/${data.memberId}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role: data.role }),
      });
    },
    onSuccess: () => {
      showSuccess('Role updated');
      queryClient.invalidateQueries({ queryKey: ['members', currentOrg?.id] });
    },
    onError: (err: Error) => {
      showError(err.message ?? 'Failed to update role');
    },
  });

  // Add actions column
  const columnsWithActions: ColumnDef<OrgMember>[] = [
    ...memberColumns,
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const member = row.original;
        if (member.role === 'owner') return null; // Can't modify owner
        return (
          <div className="flex items-center gap-2">
            <Select
              value={member.role}
              onValueChange={(role) => role && roleMutation.mutate({ memberId: member.id, role })}
            >
              <SelectTrigger className="h-8 w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMemberToRemove(member)}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  if (error) {
    showError('Failed to load members');
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Members</h1>
          <p className="text-sm text-neutral-400">
            Manage your organization members and their roles.
          </p>
        </div>
        <Button onClick={() => setShowInviteDialog(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite member
        </Button>
      </div>

      {isLoading ? (
        <TableSkeleton cols={5} rows={5} />
      ) : (
        <DataTable columns={columnsWithActions} data={members} />
      )}

      {/* Invite dialog — ui-002: react-hook-form replaces useState */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite member</DialogTitle>
            <DialogDescription>
              Send an invitation to join {currentOrg?.name ?? 'your organization'}.
            </DialogDescription>
          </DialogHeader>
          <Form {...inviteForm}>
            <form
              onSubmit={inviteForm.handleSubmit((values) =>
                inviteMutation.mutate(values),
              )}
              className="flex flex-col gap-3 py-2"
            >
              <FormField
                control={inviteForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email address</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="colleague@company.com"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      We'll send an invitation to this email address.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={inviteForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Role</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={
                    inviteMutation.isPending ||
                    !inviteForm.formState.isValid
                  }
                >
                  {inviteMutation.isPending ? 'Sending...' : 'Send invitation'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Remove confirmation */}
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove member?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {memberToRemove?.email} from {currentOrg?.name}. They will lose
              access to all resources in this organization.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => memberToRemove && removeMutation.mutate(memberToRemove.id)}
              disabled={removeMutation.isPending}
              className="bg-red-600 hover:bg-red-500"
            >
              {removeMutation.isPending ? 'Removing...' : 'Remove member'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
